import { endOfDay, startOfDay, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import useTimeZone from '@swf/core/hooks/useTimeZone';
import { select } from '@swf/core/store';

export const FROM_DATE_OFFSET = 7;

const getMinDate = (comparisonDate: Date, launchDate: Date): Date => {
  const isLaunchDateSoonerThanComparisonDate = launchDate.getTime() - comparisonDate.getTime() > 0;

  if (isLaunchDateSoonerThanComparisonDate) {
    return comparisonDate;
  }

  return launchDate;
};

const useDateFilters = () => {
  const { timeZoneName } = useTimeZone();
  const configLaunchDate = useSelector(select.cms.getWebsiteLaunchDate);

  const { endOfDayToday, filterStartDate, minDate, maxDate } = useMemo(() => {
    const endOfDayToday = endOfDay(utcToZonedTime(new Date().toISOString(), timeZoneName));
    const filterStartDate = startOfDay(subDays(endOfDayToday, FROM_DATE_OFFSET));

    const zonedLaunchDate = utcToZonedTime(
      configLaunchDate ?? new Date().toISOString(),
      timeZoneName
    );
    const launchDate = startOfDay(zonedLaunchDate);

    return {
      endOfDayToday,
      filterStartDate,
      minDate: getMinDate(filterStartDate, launchDate),
      maxDate: endOfDayToday
    };
  }, [timeZoneName, configLaunchDate]);

  const [fromDate, setFromDate] = useState(filterStartDate);
  const [toDate, setToDate] = useState(endOfDayToday);

  const handlers = useMemo(
    () => ({
      selectFromDate: (from: Date) => setFromDate(startOfDay(from)),
      selectToDate: (to: Date) => setToDate(endOfDay(to))
    }),
    []
  );

  useEffect(() => {
    if (fromDate.getTime() < minDate.getTime()) {
      setFromDate(minDate);
    }
  }, [fromDate, minDate]);

  return {
    fromDate,
    toDate,
    ...handlers,
    minDate,
    maxDate
  };
};

export default useDateFilters;
